import React, { useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import EmptyState from './components/EmptyState';
import Description from './components/Description';
import FBConfirmationModal from './components/FBConfirmationModal';
import FBSDKIframeLoader, { initiateFBLogin, useFBSDKIframeListener } from './components/FBSDKIframeLoader';
import accountsResource, { AccountType, createFacebookAccount } from 'src/actions/accounts/accountsResource';
import agentsResource from 'src/actions/agent/agentsResource';
import { accountsFilter, findAccountCreatorName, findUnassociatedFacebookPages } from './utils/utils';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import useResource, { allFinished, allSucceeded } from 'src/presentation/hooks/useResource';
import { RowActionContainer, StyledTD, StyledTR } from 'src/presentation/components/pages/shared/Table';
import TablePage, { TablePageRender } from '../shared/TablePage';
import { PrimaryButton } from '@ad-scripts/presentation/components/common/button';
import { toastError } from 'src/actions/toast';
import systemConfigurationResource from 'src/actions/systemConfiguration/systemConfigurationResource';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';
import RowActions from './components/RowActions';

const dataAidPrefix = 'facebookPages';
const description = `Connect with your Gladly customers through Facebook Pages. To learn more, visit`;
const emptyStateCopy = 'Need help setting up Facebook Pages on Gladly? Check out our';
const emptyStateLinkText = 'Help Docs';
const docsLink = 'https://help.gladly.com/docs/set-up-and-configure-facebook-messenger';

const columnConfig = [
  { attr: 'pageName', label: 'Page Name' },
  { attr: 'facebookPageId', label: 'Facebook Page ID' },
  { attr: 'addedBy', label: 'Added By' },
  { attr: 'addedOn', label: 'Added On' },
];

const FacebookConfigurations: TablePageRender = props => {
  const dispatch = useDispatch();
  const { loadingState: agentsLoadingState, values: agents = [] } = useResource(agentsResource);
  const {
    loadingState: accountLoadingState,
    values: accounts = [],
    errors: errors,
  } = useResource(accountsResource, {
    fetch: true,
  });
  const { loadingState: sysConfigLoadingState, value: sysConfig } = useDocumentResource(systemConfigurationResource, {
    fetch: true,
  });

  const filteredAccounts = accounts.filter(account => account.type === AccountType.FacebookMessenger);

  const iframeRef = useRef();
  const [fbPages, setFbPages, fbError, setFbError] = useFBSDKIframeListener({
    targetOrigin: sysConfig.publicUrl,
  });

  const selectedFbPages = useMemo(
    () => findUnassociatedFacebookPages(accounts, fbPages, AccountType.FacebookMessenger),
    [accounts, fbPages]
  );

  const onClick = () => {
    initiateFBLogin({ iframeRef, targetOrigin: sysConfig.publicUrl });
  };

  const onConfirmationOk = () => {
    selectedFbPages.forEach(({ access_token, id, name }) => {
      const customToastMsg = `Successfully added Facebook page ${name}`;
      dispatch(
        accountsResource.effects.add({}, createFacebookAccount(id, access_token, name), {
          disableWindowBack: true,
          customToastMsg,
        })
      );
    });
    setFbPages();
  };

  const onConfirmationCancel = () => {
    setFbPages();
  };

  const renderConfirmationModal = () => {
    if (fbPages) {
      if (selectedFbPages.length === 0) {
        return null;
      }
      return (
        <FBConfirmationModal
          description={'Please confirm the changes to Facebook Pages'}
          selectedFBPages={selectedFbPages}
          onConfirmationCancel={onConfirmationCancel}
          onConfirmationOk={onConfirmationOk}
          title={'Create pages'}
        />
      );
    }
    return null;
  };

  if (fbError) {
    dispatch(toastError('Failed to connect with Facebook'));
    setFbError();
  }

  const renderRow = account => {
    return (
      <StyledTR data-aid={`${dataAidPrefix}-row`} key={account.id}>
        <StyledTD key={`${dataAidPrefix}cell-order-${account.id}`}>{account.content.name}</StyledTD>
        <StyledTD data-aid={`${dataAidPrefix}-cell-${account.id}`} key={`${dataAidPrefix}cell-${account.id}`}>
          {account.content.id}
        </StyledTD>
        <StyledTD data-aid={`${dataAidPrefix}-cell-${account.id}`}>{findAccountCreatorName(account, agents)}</StyledTD>
        <StyledTD data-aid={`${dataAidPrefix}-cell-${account.id}`}>{toDateTimeString(account.createdAt)}</StyledTD>
        <StyledTD>
          <RowActionContainer>
            <RowActions item={account} />
          </RowActionContainer>
        </StyledTD>
      </StyledTR>
    );
  };

  return (
    <>
      {renderConfirmationModal()}
      {allSucceeded(sysConfigLoadingState) ? (
        <FBSDKIframeLoader
          accountType={AccountType.FacebookMessenger}
          iframeRef={iframeRef}
          publicUrl={sysConfig.publicUrl}
        />
      ) : null}
      <TablePage
        actions={
          filteredAccounts.length !== 0 ? (
            <PrimaryButton data-aid={`${dataAidPrefix}-addNew`} onClick={onClick}>
              Manage Facebook Pages
            </PrimaryButton>
          ) : null
        }
        columnConfig={columnConfig}
        data-aid={`${dataAidPrefix}-page`}
        description={
          <Description description={description} helpDocsLink={docsLink} linkText={'Guide to Create Pages'} />
        }
        emptyStateRender={
          <EmptyState
            onButtonClick={onClick}
            buttonText={'Add Facebook Pages'}
            copy={emptyStateCopy}
            docsLink={docsLink}
            linkText={emptyStateLinkText}
          />
        }
        errors={errors}
        filterFunc={accountsFilter}
        isLoading={!allFinished(accountLoadingState, agentsLoadingState)}
        items={filteredAccounts}
        name="Facebook Pages"
        renderRow={renderRow}
        resetErrors={accountsResource.actions.resetErrors}
        root={props.root}
      />
    </>
  );
};

export default FacebookConfigurations;
